import { uniqueId } from 'lodash';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Flex,
  Pagination,
  reactIcons as Icons,
  Tag,
  Typography,
} from '@project44/manifest-ds';
import { Skeleton } from '@project44/shared-components';
import { useTranslation } from '@project44/translations-utils';

const styles = {
  container: {
    gap: '$medium',
    minWidth: '100%',
    padding: '$x-small $large',
    '@media (max-width: 719px)': {
      justifyContent: 'center',
    },
  },
};

const fits = (cardCount: number) => {
  const padding = 48; // '0 $large' container padding
  const gap = (cardCount - 1) * 16; // $medium gaps between cards
  const card = cardCount * 300; // sum of minimum card widths
  return card + gap + padding; // Total width required for cardCount
};

const getPerPage = (width: number) => {
  for (const per of [6, 4, 3, 2, 1]) {
    if (width >= fits(per)) return per;
  }
  return 1;
};

interface ICarousel {
  children: JSX.Element | JSX.Element[];
  defaultCard?: JSX.Element;
  title: string;
  titleColor?: string;
  isLoading?: boolean;
  showButtons?: boolean;
}

function Carousel({
  children,
  title,
  defaultCard,
  titleColor = '',
  isLoading,
  // TODO remove when Pagination updated
  showButtons = false,
}: ICarousel) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(1);

  const [shown, setShown] = useState<string[]>([]);

  const onChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const [content, total, displayTotal] = useMemo(() => {
    const childs = Array.isArray(children) ? [...children] : [children];
    if (defaultCard) childs.push(defaultCard);
    const count = childs.length;
    return [childs, count, defaultCard ? count - 1 : count];
  }, [children]);

  const contentPages = useMemo(() => {
    const keys = content.map((child) => [child.key, child.props.onShow]);
    const seenIndex = perPage * (page - 1);
    const hasShown = keys.slice(seenIndex, seenIndex + perPage);
    const firstShown = hasShown.filter(([key]) => !shown.includes(key));

    if (firstShown.length) {
      const next: string[] = [];
      firstShown.forEach(([key, callback]) => {
        next.push(key);
        if (callback) callback(key);
      });
      setShown([...new Set([...shown, ...next])].filter(Boolean));
    }

    const pages = [];
    for (let i = 0; i < content.length; i += perPage) {
      const chunk = content.slice(i, i + perPage);
      if (chunk.length < perPage) {
        chunk.push(
          ...[...Array(perPage - chunk.length)].map(() => (
            <div key={uniqueId()} style={{ flex: 1 }} />
          )),
        );
      }
      pages.push(
        <Flex key={uniqueId()} css={styles.container}>
          {chunk}
        </Flex>,
      );
    }
    return pages;
  }, [content, perPage, page]);

  useLayoutEffect(() => {
    if (containerRef && containerRef.current) {
      const { width } = containerRef.current.getBoundingClientRect();
      setPerPage(getPerPage(width));
    }
  }, []);

  useLayoutEffect(() => {
    const adjustSize = () => {
      if (containerRef && containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        const nextPer = getPerPage(width);
        if (nextPer !== perPage) {
          const maxPage = Math.ceil(total / nextPer);
          if (page > maxPage) {
            setPage(maxPage);
          }
          setPerPage(nextPer);
        }
      }
    };
    window.addEventListener('resize', adjustSize);
    return () => {
      window.removeEventListener('resize', adjustSize);
    };
  });

  return (
    <Box ref={containerRef}>
      <Flex
        gap="small"
        css={{ color: titleColor, padding: '0 $large $medium' }}
      >
        <Typography variant="bodyBold">{title}</Typography>
        {isLoading ? (
          <Skeleton css={{ width: '24px', height: '24px' }} />
        ) : (
          <Tag css={{ color: titleColor }}>{displayTotal}</Tag>
        )}
      </Flex>
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <Flex
          css={{
            transitionDuration: '0.5s',
            transform: `translateX(${(page - 1) * -100}%)`,
          }}
        >
          {contentPages}
        </Flex>
      </div>
      <Flex gap="small" justify="end" css={{ padding: '$small $large' }}>
        {showButtons && (
          <Button
            aria-label={t('common.goToCarouselFirstPage')}
            isDisabled={page === 1}
            onPress={() => setPage(1)}
            size="small"
            variant="secondary"
          >
            <Icons.ChevronLeftDouble />
          </Button>
        )}
        <Pagination
          onChange={onChange}
          page={page}
          rowsPerPage={perPage}
          showPageNumbers={false}
          totalRowCount={total}
        />
        {showButtons && (
          <Button
            aria-label={t('common.goToCarouselLastPage')}
            isDisabled={page === Math.ceil(total / perPage)}
            onPress={() => setPage(Math.ceil(total / perPage))}
            size="small"
            variant="secondary"
          >
            <Icons.ChevronRightDouble />
          </Button>
        )}
      </Flex>
    </Box>
  );
}

export default Carousel;
